import { type IPoint } from 'fabric/fabric-impl';
import { type Rect } from '../types';
import { fabric } from 'fabric';

// Square of the distance between two points.
function sqrDistanceBetweenPoints(point: IPoint, x: number, y: number) {
  const dx = x - point.x;
  const dy = y - point.y;
  return dx * dx + dy * dy;
}

// Test if the gap between 2 points is larger than a threshold
function distanceBetweenPointsIsGreaterThan(
  p0: IPoint,
  p1: IPoint,
  threshold: number
): boolean {
  return sqrDistanceBetweenPoints(p0, p1.x, p1.y) > threshold * threshold;
}

// Returns true if the point is within radius of the line segment.
function pointNearLine(
  point: IPoint,
  start: IPoint,
  end: IPoint,
  radius: number
) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const l2 = dx * dx + dy * dy;
  const radiusSqr = radius * radius;

  // Line has zero length.
  if (l2 === 0) {
    return sqrDistanceBetweenPoints(point, start.x, start.y) < radiusSqr;
  }

  // Check endpoints.
  if (sqrDistanceBetweenPoints(point, start.x, start.y) < radiusSqr) {
    return true;
  }
  if (sqrDistanceBetweenPoints(point, end.x, end.y) < radiusSqr) return true;

  let t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));

  return (
    sqrDistanceBetweenPoints(point, start.x + t * dx, start.y + t * dy) <
    radiusSqr
  );
}

function pointInRect(point: IPoint, rect: Rect) {
  return (
    point.x >= rect.left &&
    point.x <= rect.left + rect.width &&
    point.y >= rect.top &&
    point.y <= rect.top + rect.height
  );
}

/** Return all objects in the provided context that overlap with this one */
function overlappingObjects(
  context: fabric.Object[],
  obj: fabric.Object,
  absolute?: boolean,
  calculate?: boolean
) {
  return context.filter(other => {
    return (
      obj !== other && obj.intersectsWithObject(other, absolute, calculate)
    );
  });
}

// This is an approximation since it implies a square around the point.
function pointInRectWithRadius(point: IPoint, rect: Rect, radius: number) {
  return (
    point.x + radius >= rect.left &&
    point.x - radius <= rect.left + rect.width &&
    point.y + radius >= rect.top &&
    point.y - radius <= rect.top + rect.height
  );
}

function lineLength(p0: IPoint, p1: IPoint) {
  const x = p0.x - p1.x;
  const y = p0.y - p1.y;
  return Math.sqrt(x * x + y * y);
}

// Bezier curve functions.
// From fabric's path.js
function CB1(t: number) {
  return t * t * t;
}
function CB2(t: number) {
  return 3 * t * t * (1 - t);
}
function CB3(t: number) {
  return 3 * t * (1 - t) * (1 - t);
}
function CB4(t: number) {
  return (1 - t) * (1 - t) * (1 - t);
}

function getPointOnCubicBezierIterator(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x: number,
  p4y: number
) {
  return function (pct: number) {
    const c1 = CB1(pct);
    const c2 = CB2(pct);
    const c3 = CB3(pct);
    const c4 = CB4(pct);
    return {
      x: p4x * c1 + p3x * c2 + p2x * c3 + p1x * c4,
      y: p4y * c1 + p3y * c2 + p2y * c3 + p1y * c4,
    };
  };
}

function getTangentCubicIterator(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x: number,
  p4y: number
) {
  return function (pct: number) {
    const invT = 1 - pct;
    const tangentX =
      3 * invT * invT * (p2x - p1x) +
      6 * invT * pct * (p3x - p2x) +
      3 * pct * pct * (p4x - p3x);
    const tangentY =
      3 * invT * invT * (p2y - p1y) +
      6 * invT * pct * (p3y - p2y) +
      3 * pct * pct * (p4y - p3y);
    return Math.atan2(tangentY, tangentX);
  };
}

function pointNearCubicBezier(
  point: IPoint,
  radius: number,
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x: number,
  p4y: number
) {
  const iterator = getPointOnCubicBezierIterator(
    p1x,
    p1y,
    p2x,
    p2y,
    p3x,
    p3y,
    p4x,
    p4y
  );
  const lengthEstimate = _lengthOfPath(p1x, p1y, p2x, p2y, p3x, p3y, p4x, p4y);
  return _searchIteratorForPoint(
    iterator,
    point,
    radius,
    lengthEstimate / radius
  );
}

function QB1(t: number) {
  return t * t;
}

function QB2(t: number) {
  return 2 * t * (1 - t);
}

function QB3(t: number) {
  return (1 - t) * (1 - t);
}

function getPointOnQuadraticBezierIterator(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number
) {
  return function (pct) {
    const c1 = QB1(pct);
    const c2 = QB2(pct);
    const c3 = QB3(pct);
    return {
      x: p3x * c1 + p2x * c2 + p1x * c3,
      y: p3y * c1 + p2y * c2 + p1y * c3,
    };
  };
}

function getTangentQuadraticIterator(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number
) {
  return function (pct: number) {
    const invT = 1 - pct;
    const tangentX = 2 * invT * (p2x - p1x) + 2 * pct * (p3x - p2x);
    const tangentY = 2 * invT * (p2y - p1y) + 2 * pct * (p3y - p2y);
    return Math.atan2(tangentY, tangentX);
  };
}

function pointNearQuadraticBezier(
  point: IPoint,
  radius: number,
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number
) {
  const iterator = getPointOnQuadraticBezierIterator(
    p1x,
    p1y,
    p2x,
    p2y,
    p3x,
    p3y
  );
  const lengthEstimate = _lengthOfPath(p1x, p1y, p2x, p2y, p3x, p3y);
  return _searchIteratorForPoint(
    iterator,
    point,
    radius,
    lengthEstimate / radius
  );
}

function _lengthOfPath(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x?: number,
  p4y?: number
) {
  let length = 0;
  length += lineLength({ x: p1x, y: p1y }, { x: p2x, y: p2y });
  length += lineLength({ x: p2x, y: p2y }, { x: p3x, y: p3y });
  if (p4x && p4y) {
    length += lineLength({ x: p3x, y: p3y }, { x: p4x, y: p4y });
  }
  return length;
}

function _searchIteratorForPoint(
  iterator: (i: number) => IPoint,
  point: IPoint,
  radius: number,
  steps: number
) {
  const radiusSqr = radius * radius;

  for (let i = 0; i <= 1; i += 1 / steps) {
    const bezierPoint = iterator(i);
    if (
      sqrDistanceBetweenPoints(point, bezierPoint.x, bezierPoint.y) <= radiusSqr
    ) {
      return true;
    }
  }

  return false;
}

// Constrain {point} to the nearest {axisInterval} angle axis about {relativeToPoint}
function constrainToAxis(
  point: IPoint,
  relativeToPoint: IPoint,
  axisInterval = Math.PI / 4
) {
  const dx = point.x - relativeToPoint.x;
  const dy = point.y - relativeToPoint.y;
  const hypotLength = Math.hypot(dx, dy);
  const hypotAngle = Math.atan2(dy, dx);
  const angle =
    Math.round((hypotAngle % (Math.PI * 2)) / axisInterval) * axisInterval;

  const area = 0.5 * hypotLength * hypotLength * Math.sin(hypotAngle - angle);
  const h = (2 * area) / hypotLength;
  const length = Math.sqrt(hypotLength ** 2 - h ** 2);

  return {
    x: relativeToPoint.x + length * Math.cos(angle),
    y: relativeToPoint.y + length * Math.sin(angle),
    angle,
  };
}

function combinedPosition(positions: Rect[]): Rect {
  const left = Math.min(...positions.map(o => o.left));
  const top = Math.min(...positions.map(o => o.top));

  return {
    left,
    top,
    width: Math.max(...positions.map(o => o.left + o.width)) - left,
    height: Math.max(...positions.map(o => o.top + o.height)) - top,
  };
}

function pointsToRect(points: IPoint[]): Rect {
  return combinedPosition(
    points.map(p => ({ left: p.x, top: p.y, width: 0, height: 0 }))
  );
}

/** Given one or more rects positioned relatively to a shape, determine a bounding box
 * relative to the canvas itself that contains all the rects
 */
const getBoundingBoxFromLocalRect = ({
  rect,
  borderAdjustment,
  object,
}: {
  rect: Rect | Rect[];
  object: Pick<fabric.Object, 'left' | 'top' | 'angle'>;
  borderAdjustment?: number;
}): Rect => {
  // get all points from the rotated, translated rects
  const rects = Array.isArray(rect) ? rect : [rect];
  const points = rects.flatMap(rect =>
    [
      [rect.left, rect.top],
      [rect.left + rect.width, rect.top],
      [rect.left + rect.width, rect.top + rect.height],
      [rect.left, rect.top + rect.height],
    ].map(([x, y]) =>
      fabric.util
        .rotatePoint(
          new fabric.Point(
            x + (borderAdjustment ?? 0),
            y + (borderAdjustment ?? 0)
          ),
          new fabric.Point(0, 0),
          fabric.util.degreesToRadians(object.angle ?? 0)
        )
        .addEquals(new fabric.Point(object.left, object.top))
    )
  );

  // determine the maximum x/y extents of the points, so we can create a bounding box that contains
  // all of them
  const extent = {
    minX: points[0].x,
    maxX: points[0].x,
    minY: points[0].y,
    maxY: points[0].y,
  };
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (extent.minX === undefined || extent.minX > point.x) {
      extent.minX = point.x;
    }
    if (extent.maxX === undefined || extent.maxX < point.x) {
      extent.maxX = point.x;
    }
    if (extent.minY === undefined || extent.minY > point.y) {
      extent.minY = point.y;
    }
    if (extent.maxY === undefined || extent.maxY < point.y) {
      extent.maxY = point.y;
    }
  }

  return {
    top: extent.minY,
    left: extent.minX,
    width: extent.maxX - extent.minX,
    height: extent.maxY - extent.minY,
  };
};

export {
  lineLength,
  pointNearLine,
  pointInRect,
  pointInRectWithRadius,
  distanceBetweenPointsIsGreaterThan,
  sqrDistanceBetweenPoints,
  getPointOnCubicBezierIterator,
  getTangentCubicIterator,
  pointNearCubicBezier,
  getPointOnQuadraticBezierIterator,
  getTangentQuadraticIterator,
  pointNearQuadraticBezier,
  constrainToAxis,
  combinedPosition,
  pointsToRect,
  getBoundingBoxFromLocalRect,
  overlappingObjects,
};
